import { Link } from "react-router-dom";
import { PAGES } from "../../routeConfig";
import { useAuth } from "../../Context/AuthContext";
import "../../assets/css/wes-faq.css";

const buildSections = (isGuest) => [
  {
    id: "faq-accounts",
    title: "Trade accounts",
    items: [
      {
        q: "Who can buy from WES?",
        a: (
          <p>
            WES Australasia is a business-to-business distributor. Our pricing and services are designed for
            professional customers who purchase regularly, such as installers, technicians, integrators, retailers and
            organisations.
          </p>
        ),
      },
      isGuest && {
        q: "How do I open a trade account?",
        a: (
          <p>
            Apply online through our <Link to={PAGES.SIGNUP}>trade account application</Link>. Trade customers can
            access online ordering, account pricing, order history and other tools designed to make purchasing easier.
          </p>
        ),
      },
    ],
  },
  {
    id: "faq-delivery",
    title: "Delivery",
    items: [
      {
        q: "What delivery options are available?",
        a: (
          <>
            <p>Delivery and handling charges apply to all orders. Options include:</p>
            <ul>
              <li>
                <strong>Standard Mail Parcel Post</strong> — ideal for light parcels, e.g. up to 200 grams.
              </li>
              <li>
                <strong>Standard Sydney Metro</strong> — usually shipped the same day in the afternoon and delivered the
                next working day. Our most economical service.
              </li>
              <li>
                <strong>Standard Australia Wide</strong> — road service; delivery times vary by location. Suits heavy and
                bulky goods and dangerous goods.
              </li>
              <li>
                <strong>Express</strong> — capital cities usually within 1–2 working days, remote areas 2–4 working days.
                Not available for dangerous goods.
              </li>
              <li>
                <strong>International</strong> — international mail and express shipping, or nominate your own transport.
              </li>
              <li>
                <strong>Drop shipment</strong> — online orders only; we send the goods directly to your customer with a
                picking slip, and email the invoice to you.
              </li>
              <li>
                <strong>Courier pick-up</strong> — organise your own courier and add their details in the order comments.
              </li>
            </ul>
          </>
        ),
      },
      {
        q: "What is the cut-off for same-day despatch?",
        a: <p>For Express orders, order before 2pm (Sydney time) for same-day despatch.</p>,
      },
      {
        q: "Can you deliver to a PO Box or parcel locker?",
        a: (
          <p>
            Deliveries to PO Boxes and parcel lockers result in additional charges and are not recommended. If a PO Box
            is the only address option, the goods will be shipped and any additional costs passed on.
          </p>
        ),
      },
      {
        q: "Why is my order going by road?",
        a: (
          <p>
            Orders with restricted goods, such as some batteries, aerosols or isopropanol products, must be sent by road
            service.
          </p>
        ),
      },
      {
        q: "What happens if an item is out of stock?",
        a: (
          <p>
            Goods not in stock are automatically back ordered and shipped when available with your next order, unless
            you request otherwise.
          </p>
        ),
      },
      {
        q: "How do I track my order?",
        a: (
          <p>
            A tracking number is provided with every dispatched order. If your order hasn’t arrived by the expected date,
            check the tracking details and contact the carrier first. If the carrier can’t locate your parcel, contact
            us in writing within 10 days of the expected delivery date with your invoice number, tracking number and
            details of your contact with the carrier.
          </p>
        ),
      },
    ],
  },
  {
    id: "faq-click-collect",
    title: "Click & Collect",
    items: [
      {
        q: "How does Click & Collect work?",
        a: (
          <p>
            Pick up from our showroom trade counter at 84-90 Parramatta Road, Summer Hill NSW 2130. Please allow 2 hours
            after placing your order. We’ll notify you by SMS and/or email when it’s ready to collect.
          </p>
        ),
      },
      {
        q: "When can I pick up?",
        a: (
          <p>
            Monday to Friday 8.30am to 5.00pm, and Saturday 9.00am to 2.00pm. Closed Sundays and public holidays. For a
            Saturday pick-up, please order on Friday, as our call centre is closed on Saturday. If you need us on
            Saturday, call <a href="tel:+61297989233">02 9798 9233</a>.
          </p>
        ),
      },
    ],
  },
  {
    id: "faq-returns",
    title: "Returns & order problems",
    items: [
      {
        q: "How do I return goods?",
        a: (
          <p>
            Obtain a Return Authorisation (RA) number before returning anything. Send goods freight prepaid with the RA
            number clearly written on the parcel. Parcels returned without an RA will not be accepted.
          </p>
        ),
      },
      {
        q: "What if a product is faulty?",
        a: (
          <p>
            Faulty goods are returned to our supplier for their determination and will be repaired, replaced or credited
            in accordance with the Australian Consumer Law. Damage caused after delivery, or by incorrect installation
            or use, is not covered.
          </p>
        ),
      },
      {
        q: "Can I return something I no longer need?",
        a: <p>Goods purchased incorrectly or no longer required cannot be returned.</p>,
      },
      {
        q: "My order arrived damaged — what should I do?",
        a: (
          <p>
            If the carton is visibly damaged, note it on the carrier’s delivery docket before signing. Report damage to
            us in writing within 7 days of delivery, with your invoice number and photos of the goods, carton,
            packaging and shipping label. Keep everything until the claim is finalised, and don’t use or install damaged
            goods.
          </p>
        ),
      },
      {
        q: "Something is missing or incorrect in my order",
        a: (
          <p>
            Check your goods against the picking slip or invoice as soon as they arrive. Report any shortage or
            incorrect item in writing (email is fine) within 7 days of delivery, with your invoice number and a list of
            the items.
          </p>
        ),
      },
    ],
  },
  {
    id: "faq-privacy",
    title: "Privacy & emails",
    items: [
      {
        q: "How do I stop marketing emails?",
        a: <p>You can unsubscribe at any time using the link in our emails, or by contacting us.</p>,
      },
      {
        q: "How is my information used?",
        a: (
          <p>
            We never sell or rent your personal information. See our <Link to={PAGES.PRIVACYPOLICY}>Privacy Policy</Link>{" "}
            for details.
          </p>
        ),
      },
    ],
  },
];

export const FAQ = () => {
  const { isAuthenticated } = useAuth();
  const sections = buildSections(isAuthenticated === false).map((section) => ({
    ...section,
    items: section.items.filter(Boolean),
  }));

  return (
    <main className="wes-faq-content">
      <section className="wes-faq-style-1">
        <div className="wes-faq-style-2">
          <nav aria-label="Breadcrumb" className="wes-faq-style-3">
            <Link to="/" className="wes-faq-style-4">
              Home
            </Link>
            <span aria-hidden="true">›</span>
            <span className="wes-faq-style-5">Help</span>
          </nav>
          <div className="wes-faq-style-6">
            <span className="wes-faq-style-7">Help centre</span>
            <h1 className="wes-faq-style-8">How can we help?</h1>
            <p className="wes-faq-style-9">
              Answers to common questions about trade accounts, delivery, Click &amp; Collect and returns. Can’t find
              what you need? <Link to={PAGES.CONTACTUS}>Contact our team</Link>.
            </p>
          </div>
        </div>
      </section>

      <section>
        <div className="wes-faq-style-10">
          <div className="wes-legal">
            <nav className="wes-toc" aria-label="Help topics">
              <ol>
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>
                      <span className="n">{String(index + 1).padStart(2, "0")}</span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            <article className="wes-legal-body">
              {sections.map((section) => (
                <section id={section.id} className="wes-legal-sec" key={section.id}>
                  <h2 className="tc-h">{section.title}</h2>
                  {section.items.map((item) => (
                    <div className="wes-faq-item" key={item.q}>
                      <h3 className="wes-faq-q">{item.q}</h3>
                      {item.a}
                    </div>
                  ))}
                </section>
              ))}

              <section id="faq-contact" className="wes-legal-sec">
                <div className="wes-g2 wes-faq-style-11">
                  <div className="wes-faq-style-12">
                    <h2 className="tc-h">STILL NEED HELP?</h2>
                    <p>
                      Phone <a href="tel:+61297979866">(02) 9797 9866</a>
                    </p>
                    <p>
                      Email <a href="mailto:sales@wes.net.au">sales@wes.net.au</a>
                    </p>
                    <p>
                      Or <Link to={PAGES.CONTACTUS}>send us a message</Link>
                    </p>
                  </div>
                  <div className="wes-faq-style-12">
                    <h2 className="tc-h">BUSINESS HOURS:</h2>
                    <p>Monday to Friday 8.30am to 5.00pm.</p>
                    <p>Saturday 9.00am to 2.00pm (trade counter only). Closed Sundays and Public Holidays.</p>
                  </div>
                </div>
              </section>

              <p>
                Full details are in our <Link to={PAGES.TERMSANDCONDITIONS}>Terms and Conditions</Link>.
              </p>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
};
